/**
 * Course Player JavaScript
 * Handles AJAX lesson loading and navigation
 */

(function() {
    'use strict';

    class CoursePlayer {
        constructor() {
            this.courseId = null;
            this.currentLessonId = null;
            this.sidebar = null;
            this.content = null;
            this.videoManager = null;
            // In-memory cache of fetched lessons (id → response data) plus the set
            // of in-flight requests. Revisited or prefetched lessons render
            // instantly without another round-trip — each request otherwise pays
            // the full WordPress + plugin bootstrap server-side.
            this.cache = new Map();
            this.inflight = new Map();

            this.init();
        }

        init() {
            this.sidebar = document.querySelector('.lms-course-sidebar');
            this.content = document.querySelector('.lms-lesson-content');

            if (!this.sidebar || !this.content) {
                return;
            }

            this.courseId = this.sidebar.dataset.courseId;
            this.currentLessonId = this.content.dataset.lessonId;

            this.bindEvents();
            this.initBrowserNavigation();
            this.initVideoTracking();
        }

        /**
         * Initialize video position tracking
         */
        async initVideoTracking() {
            // Dynamically import VideoPlayerManager
            try {
                const { VideoPlayerManager } = await import('./video/VideoPlayerManager.js');
                this.videoManager = new VideoPlayerManager(
                    this.currentLessonId,
                    lmsPlayer.restUrl,
                    lmsPlayer.nonce
                );
                await this.videoManager.init();
            } catch (error) {
                console.error('Failed to initialize video tracking:', error);
            }
        }

        bindEvents() {
            // Lesson click in sidebar (the link only — the completion toggle is separate)
            document.addEventListener('click', (e) => {
                const lessonLink = e.target.closest('.lms-lesson-item__link');
                if (lessonLink) {
                    e.preventDefault();
                    const lessonId = lessonLink.dataset.lessonId;
                    this.loadLesson(lessonId);
                    this.closeSidebar();
                }
            });

            // Completion toggle in the sidebar (the circle button)
            document.addEventListener('click', (e) => {
                const circle = e.target.closest('button.lms-lesson-item__completion');
                if (circle) {
                    e.preventDefault();
                    this.toggleCompletion(circle.dataset.lessonId);
                }
            });

            // Navigation buttons
            document.addEventListener('click', (e) => {
                const navButton = e.target.closest('.lms-nav-button--prev, .lms-nav-button--next');
                if (navButton) {
                    e.preventDefault();
                    const lessonId = navButton.dataset.lessonId;
                    if (lessonId) {
                        this.loadLesson(lessonId);
                    }
                }
            });

            // Note: collection member drill-down is a normal server-rendered page
            // (card links are real permalinks; the guard routes them into the
            // player as ?member=). This keeps GenerateBlocks local styles intact,
            // so there is deliberately no AJAX interception for member cards here.

            // Complete button toggle
            document.addEventListener('click', (e) => {
                const completeButton = e.target.closest('.lms-nav-button--complete');
                if (completeButton) {
                    e.preventDefault();
                    this.toggleCompletion(completeButton.dataset.lessonId);
                }
            });

            // Module toggle
            document.addEventListener('click', (e) => {
                const moduleHeader = e.target.closest('.lms-module__header');
                if (moduleHeader) {
                    const module = moduleHeader.closest('.lms-module');
                    if (module) {
                        module.classList.toggle('lms-module--collapsed');
                    }
                }
            });

            // Sidebar close button
            document.addEventListener('click', (e) => {
                const closeButton = e.target.closest('.lms-sidebar-close');
                if (closeButton) {
                    this.closeSidebar();
                }
            });

            // Overlay click to close sidebar
            document.addEventListener('click', (e) => {
                const overlay = e.target.closest('.lms-course-player__overlay');
                if (overlay) {
                    this.closeSidebar();
                }
            });
        }

        closeSidebar() {
            const player = document.getElementById('lms-course-player');
            const sidebar = document.getElementById('lms-course-sidebar');
            const overlay = document.querySelector('.lms-course-player__overlay');
            const toggle = document.querySelector('.lms-nav-button--menu');

            if (player) {
                player.classList.remove('lms-course-player--sidebar-open');
                document.body.classList.remove('lms-course-player--sidebar-open');
            }

            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
            }

            if (sidebar) {
                sidebar.setAttribute('aria-hidden', 'true');
            }

            if (overlay) {
                overlay.setAttribute('aria-hidden', 'true');
            }
        }

        loadLesson(lessonId) {
            lessonId = lessonId != null ? String(lessonId) : '';
            if (!lessonId || lessonId === String(this.currentLessonId)) {
                return;
            }

            // Cache hit → render immediately: no spinner, no round-trip.
            if (this.cache.has(lessonId)) {
                this.applyLesson(lessonId, this.cache.get(lessonId));
                return;
            }

            // Cache miss → fetch (with a loading state), then render + cache.
            this.content.classList.add('lms-lesson-content--loading');
            this.fetchLesson(lessonId)
                .then((data) => {
                    this.applyLesson(lessonId, data);
                })
                .catch(() => {
                    alert(lmsPlayer.strings.error);
                })
                .finally(() => {
                    this.content.classList.remove('lms-lesson-content--loading');
                });
        }

        /**
         * Fetch a lesson's content and cache it. De-dupes concurrent requests for
         * the same lesson (e.g. a click racing an in-flight prefetch). Resolves
         * with the response `data`; rejects on error.
         */
        fetchLesson(lessonId) {
            lessonId = String(lessonId);
            if (this.cache.has(lessonId)) {
                return Promise.resolve(this.cache.get(lessonId));
            }
            if (this.inflight.has(lessonId)) {
                return this.inflight.get(lessonId);
            }
            const params = new URLSearchParams({ course_id: this.courseId });
            const promise = fetch(`${lmsPlayer.restUrl}sleeklms/v1/lessons/${lessonId}/content?${params}`, {
                method: 'GET',
                headers: { 'X-WP-Nonce': lmsPlayer.nonce }
            })
                .then((response) => response.json())
                .then((response) => {
                    if (response.success && response.data) {
                        this.cache.set(lessonId, response.data);
                        return response.data;
                    }
                    throw new Error('bad_response');
                })
                .finally(() => {
                    this.inflight.delete(lessonId);
                });
            this.inflight.set(lessonId, promise);
            return promise;
        }

        /** Render a lesson + update URL/sidebar/state, then prefetch its neighbours. */
        applyLesson(lessonId, data) {
            this.renderLesson(data);
            this.updateURL(lessonId);
            this.updateSidebarActive(lessonId);
            this.currentLessonId = lessonId;
            this.prefetchAdjacent(data.navigation);
        }

        /**
         * Warm the cache with the previous/next lessons in the background so the
         * most likely next click is instant. Fire-and-forget — a failed prefetch
         * just means that lesson fetches normally when clicked.
         */
        prefetchAdjacent(navigation) {
            if (!navigation) {
                return;
            }
            [navigation.next, navigation.prev].forEach((id) => {
                if (!id) {
                    return;
                }
                const key = String(id);
                if (!this.cache.has(key) && !this.inflight.has(key)) {
                    this.fetchLesson(key).catch(() => {});
                }
            });
        }

        async renderLesson(data) {
            // If we AJAX-navigate away from a server-rendered member view, drop its
            // "back to collection" link (it lives in the header, not the body).
            const staleBack = this.content.querySelector('.lms-course-player__header .lms-collection-back');
            if (staleBack) {
                staleBack.remove();
            }

            // Update lesson title
            const title = this.content.querySelector('.lms-lesson__title');
            if (title) {
                title.textContent = data.title;
            }

            // Module name above the title (course header mode only).
            const moduleName = this.content.querySelector('.lms-lesson__module');
            if (moduleName && typeof data.module_title === 'string') {
                moduleName.textContent = data.module_title;
            }

            // Update lesson content
            const body = this.content.querySelector('.lms-lesson__body');
            if (body) {
                // If lesson is locked, show lock message instead of content
                if (data.is_locked) {
                    body.innerHTML = `<div class="lms-lesson-locked-message">
                        <span class="lms-lesson-locked-message__icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16.933 16.933"><g stroke-linejoin="round"><path d="M8.467.793a2.916 2.916 0 0 0-2.91 2.912V6.35h.529V3.705a2.38 2.38 0 0 1 2.38-2.383 2.38 2.38 0 0 1 2.382 2.383V6.35h.529V3.705A2.916 2.916 0 0 0 8.467.793"/><path d="M4.762 6.086c-1.02 0-1.852.832-1.852 1.851v6.35c0 1.02.832 1.852 1.852 1.852h7.408c1.02 0 1.853-.832 1.853-1.852v-6.35c0-1.02-.833-1.851-1.853-1.851zm0 .53h7.408a1.32 1.32 0 0 1 1.324 1.322v6.35c0 .733-.59 1.323-1.324 1.323H4.762a1.32 1.32 0 0 1-1.323-1.324v-6.35c0-.733.589-1.322 1.323-1.322"/><path d="M8.467 9.26c-.728 0-1.322.596-1.322 1.324 0 .727.594 1.322 1.322 1.322s1.322-.595 1.322-1.322S9.194 9.26 8.467 9.26m0 .53a.79.79 0 0 1 .793.794.79.79 0 0 1-.793.793.79.79 0 0 1-.793-.793.79.79 0 0 1 .793-.795"/><path d="M8.201 11.643v1.322a.265.265 0 0 0 .53 0v-1.322z"/></g></svg></span>
                        <p class="lms-lesson-locked-message__text">${data.lock_message}</p>
                    </div>`;
                } else {
                    body.innerHTML = data.content;
                }
            }

            // Update navigation if it exists on the page
            this.updateNavigation(data.navigation);

            // Update complete button state for the new lesson
            this.updateCompleteButton(data.id, data.completed, data.enable_completion);

            // Update data attributes
            this.content.dataset.lessonId = data.id;

            // Reinitialize video tracking for new lesson
            if (this.videoManager && !data.is_locked) {
                await this.videoManager.updateLesson(data.id);
            }

            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        updateNavigation(navigation) {
            const nav = document.querySelector('.lms-lesson__navigation');

            // Navigation might not exist if shortcode not placed
            if (!nav) {
                return;
            }

            // Previous button
            const prevButton = nav.querySelector('.lms-nav-button--prev');
            if (prevButton) {
                const prevHtml = navigation.prev
                    ? `<a href="?lesson_id=${navigation.prev}"
                        class="lms-nav-button lms-nav-button--prev"
                        data-lesson-id="${navigation.prev}"
                        rel="prev">
                        <span class="lms-nav-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M508.625 247.801 392.262 131.437c-4.18-4.881-11.526-5.45-16.407-1.269-4.881 4.18-5.45 11.526-1.269 16.407q.586.684 1.269 1.269l96.465 96.582H11.636C5.21 244.426 0 249.636 0 256.063s5.21 11.636 11.636 11.636H472.32l-96.465 96.465c-4.881 4.18-5.45 11.526-1.269 16.407s11.526 5.45 16.407 1.269q.684-.586 1.269-1.269l116.364-116.364c4.511-4.537 4.511-11.867-.001-16.406"/></svg></span>
                        <span class="lms-nav-button__text">${lmsPlayer.strings.previous}</span>
                    </a>`
                    : `<span class="lms-nav-button lms-nav-button--prev lms-nav-button--disabled" aria-disabled="true">
                        <span class="lms-nav-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M508.625 247.801 392.262 131.437c-4.18-4.881-11.526-5.45-16.407-1.269-4.881 4.18-5.45 11.526-1.269 16.407q.586.684 1.269 1.269l96.465 96.582H11.636C5.21 244.426 0 249.636 0 256.063s5.21 11.636 11.636 11.636H472.32l-96.465 96.465c-4.881 4.18-5.45 11.526-1.269 16.407s11.526 5.45 16.407 1.269q.684-.586 1.269-1.269l116.364-116.364c4.511-4.537 4.511-11.867-.001-16.406"/></svg></span>
                        <span class="lms-nav-button__text">${lmsPlayer.strings.previous}</span>
                    </span>`;

                prevButton.outerHTML = prevHtml;
            }

            // Next button
            const nextButton = nav.querySelector('.lms-nav-button--next');
            if (nextButton) {
                const nextHtml = navigation.next
                    ? `<a href="?lesson_id=${navigation.next}"
                        class="lms-nav-button lms-nav-button--next"
                        data-lesson-id="${navigation.next}"
                        rel="next">
                        <span class="lms-nav-button__text">${lmsPlayer.strings.next}</span>
                        <span class="lms-nav-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M508.625 247.801 392.262 131.437c-4.18-4.881-11.526-5.45-16.407-1.269-4.881 4.18-5.45 11.526-1.269 16.407q.586.684 1.269 1.269l96.465 96.582H11.636C5.21 244.426 0 249.636 0 256.063s5.21 11.636 11.636 11.636H472.32l-96.465 96.465c-4.881 4.18-5.45 11.526-1.269 16.407s11.526 5.45 16.407 1.269q.684-.586 1.269-1.269l116.364-116.364c4.511-4.537 4.511-11.867-.001-16.406"/></svg></span>
                    </a>`
                    : `<span class="lms-nav-button lms-nav-button--next lms-nav-button--disabled" aria-disabled="true">
                        <span class="lms-nav-button__text">${lmsPlayer.strings.next}</span>
                        <span class="lms-nav-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M508.625 247.801 392.262 131.437c-4.18-4.881-11.526-5.45-16.407-1.269-4.881 4.18-5.45 11.526-1.269 16.407q.586.684 1.269 1.269l96.465 96.582H11.636C5.21 244.426 0 249.636 0 256.063s5.21 11.636 11.636 11.636H472.32l-96.465 96.465c-4.881 4.18-5.45 11.526-1.269 16.407s11.526 5.45 16.407 1.269q.684-.586 1.269-1.269l116.364-116.364c4.511-4.537 4.511-11.867-.001-16.406"/></svg></span>
                    </span>`;

                nextButton.outerHTML = nextHtml;
            }
        }

        updateSidebarActive(lessonId) {
            // Remove active class from all lessons
            const allLessons = this.sidebar.querySelectorAll('.lms-lesson-item');
            allLessons.forEach(lesson => lesson.classList.remove('lms-lesson-item--active'));

            // Add active class to current lesson
            const currentLesson = this.sidebar.querySelector(`.lms-lesson-item[data-lesson-id="${lessonId}"]`);
            if (currentLesson) {
                currentLesson.classList.add('lms-lesson-item--active');
            }
        }

        /**
         * Toggle a lesson's completion. Works from either the footer button or a
         * sidebar circle — the endpoint flips the server state and returns the new
         * one, which is then applied to every UI surface. Disables whichever
         * control(s) exist for this lesson while the request is in flight.
         */
        toggleCompletion(lessonId) {
            lessonId = String(lessonId);

            const row = this.sidebar ? this.sidebar.querySelector(`.lms-lesson-item[data-lesson-id="${lessonId}"]`) : null;
            const circle = row ? row.querySelector('button.lms-lesson-item__completion') : null;
            const footerBtn = document.querySelector('.lms-nav-button--complete');
            const footerIsThis = footerBtn && String(footerBtn.dataset.lessonId) === lessonId;

            if (circle) circle.disabled = true;
            if (footerIsThis) footerBtn.disabled = true;

            const formData = new URLSearchParams();
            formData.append('lesson_id', lessonId);
            formData.append('course_id', this.courseId);

            fetch(`${lmsPlayer.restUrl}sleeklms/v1/progress/toggle`, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': lmsPlayer.nonce,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            })
            .then(response => response.json())
            .then(response => {
                if (response.success && response.data) {
                    this.applyCompletionState(lessonId, response.data.completed);
                }
            })
            .catch(() => {
                alert(lmsPlayer.strings.error);
            })
            .finally(() => {
                if (circle) circle.disabled = false;
                if (footerIsThis) footerBtn.disabled = false;
            });
        }

        /** Reflect a lesson's completion across the cache, sidebar circle/row, module, and footer button. */
        applyCompletionState(lessonId, completed) {
            lessonId = String(lessonId);

            // Keep the cache fresh so a cached revisit shows the right state.
            const cached = this.cache.get(lessonId);
            if (cached) {
                cached.completed = completed;
            }

            // Sidebar row + circle + module.
            const row = this.sidebar ? this.sidebar.querySelector(`.lms-lesson-item[data-lesson-id="${lessonId}"]`) : null;
            if (row) {
                row.classList.toggle('lms-lesson-item--completed', completed);
                const circle = row.querySelector('button.lms-lesson-item__completion');
                if (circle) {
                    circle.setAttribute('aria-pressed', completed ? 'true' : 'false');
                    circle.setAttribute('aria-label', completed ? lmsPlayer.strings.mark_incomplete : lmsPlayer.strings.mark_completed);
                }
                const module = row.closest('.lms-module');
                if (module) {
                    this.updateModuleCompletion(module);
                }
            }

            // Keep the sidebar progress summary in sync.
            this.updateCourseProgress();

            // Footer button — only when it belongs to this lesson (the one on screen).
            const footerBtn = document.querySelector('.lms-nav-button--complete');
            if (footerBtn && String(footerBtn.dataset.lessonId) === lessonId) {
                this.setCompleteButtonState(footerBtn, completed);
            }
        }

        /**
         * Recompute the sidebar progress summary from the DOM. Trackable lessons =
         * rows that carry a completion element; done = those also marked completed.
         * Mirrors the PHP that renders the bar (completion-enabled lessons only).
         */
        updateCourseProgress() {
            const progress = this.sidebar ? this.sidebar.querySelector('.lms-course-progress') : null;
            if (!progress) {
                return;
            }

            let total = 0;
            let done = 0;
            this.sidebar.querySelectorAll('.lms-lesson-item').forEach(item => {
                if (item.querySelector('.lms-lesson-item__completion, .lms-lesson-item__lock[data-trackable]')) {
                    total++;
                    if (item.classList.contains('lms-lesson-item--completed')) {
                        done++;
                    }
                }
            });

            const percent = total > 0 ? Math.round((done / total) * 100) : 0;

            const value = progress.querySelector('.lms-course-progress__percent-value');
            if (value) value.textContent = percent;

            const track = progress.querySelector('.lms-course-progress__track');
            if (track) track.setAttribute('aria-valuenow', percent);

            const fill = progress.querySelector('.lms-course-progress__fill');
            if (fill) fill.style.setProperty('--lms-progress', `${percent}%`);

            // The course header shows the same number.
            const header = document.querySelector('.lms-player-header__progress');
            if (header) {
                const headerValue = header.querySelector('.lms-player-header__percent-value');
                if (headerValue) headerValue.textContent = percent;
                const headerTrack = header.querySelector('.lms-player-header__track');
                if (headerTrack) headerTrack.setAttribute('aria-valuenow', percent);
                const headerFill = header.querySelector('.lms-player-header__fill');
                if (headerFill) headerFill.style.setProperty('--lms-progress', `${percent}%`);
            }
        }

        updateModuleCompletion(module) {
            const lessons = module.querySelectorAll('.lms-lesson-item');
            const completedLessons = module.querySelectorAll('.lms-lesson-item--completed');

            // If all lessons are completed, mark module as complete
            if (lessons.length > 0 && lessons.length === completedLessons.length) {
                module.classList.add('lms-module--completed');
            } else {
                module.classList.remove('lms-module--completed');
            }
        }

        updateCompleteButton(lessonId, isCompleted, enableCompletion) {
            const nav = document.querySelector('.lms-lesson__navigation');
            let completeButton = nav ? nav.querySelector('.lms-nav-button--complete') : null;

            // If navigation doesn't exist, nothing to do
            if (!nav) {
                return;
            }

            // If completion is disabled, remove the button if it exists
            if (!enableCompletion) {
                if (completeButton) {
                    completeButton.remove();
                }
                return;
            }

            // Completion is enabled - create button if it doesn't exist
            if (!completeButton) {
                // Icon-only, like the server-rendered one: the name is title + aria-label.
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'lms-nav-button lms-nav-button--complete';
                button.dataset.lessonId = lessonId;
                button.innerHTML = `<span class="lms-nav-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8"><path d="M7.814 1.308a.635.635 0 0 0-.897 0L2.878 5.346 1.083 3.551a.635.635 0 1 0-.897.898l2.243 2.243a.633.633 0 0 0 .898 0l4.487-4.487a.635.635 0 0 0 0-.897"/></svg></span>`;
                this.setCompleteButtonState(button, isCompleted);

                // Insert between prev and next inside the centre group (fall back to
                // the nav itself if the group markup isn't present).
                const centerGroup = nav.querySelector('.lms-nav-group--center') || nav;
                const nextButton = centerGroup.querySelector('.lms-nav-button--next');
                if (nextButton) {
                    centerGroup.insertBefore(button, nextButton);
                } else {
                    centerGroup.appendChild(button);
                }

                return;
            }

            // Button exists - update its state
            completeButton.dataset.lessonId = lessonId;
            this.setCompleteButtonState(completeButton, isCompleted);
        }

        /** Footer Complete button: state class, pressed state and its (title) name. */
        setCompleteButtonState(button, completed) {
            const label = completed ? lmsPlayer.strings.completed : lmsPlayer.strings.complete;
            button.dataset.completed = completed ? '1' : '0';
            button.classList.toggle('lms-nav-button--completed', completed);
            button.setAttribute('aria-pressed', completed ? 'true' : 'false');
            button.setAttribute('aria-label', label);
            button.title = label;
        }

        updateURL(lessonId) {
            const url = new URL(window.location);
            url.searchParams.set('lesson_id', lessonId);
            url.searchParams.delete('member'); // leaving any drilled-in member view
            window.history.pushState({ lessonId: lessonId }, '', url);
        }

        initBrowserNavigation() {
            window.addEventListener('popstate', (e) => {
                if (e.state && e.state.lessonId) {
                    this.loadLesson(e.state.lessonId);
                }
            });

            // Set initial state
            if (this.currentLessonId) {
                const url = new URL(window.location);
                window.history.replaceState({ lessonId: this.currentLessonId }, '', url);
            }
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            new CoursePlayer();
        });
    } else {
        new CoursePlayer();
    }

})();
